import { getPremiumTtsClientId } from "./premiumTts.js";

const apiBaseUrl = import.meta.env.VITE_SIELPA_PLUS_API_BASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export function isVocabBurstConfigured() {
  return Boolean(apiBaseUrl && anonKey);
}

function canUseDemoFallback() {
  return import.meta.env.DEV || import.meta.env.VITE_VOCAB_BURST_DEMO === "true";
}

function fillBlank(sentence, value) {
  const replacement = String(value || "").trim();
  if (!sentence || !replacement) return "";
  return String(sentence)
    .replace(/_{2,}/g, replacement)
    .replace(/\s{2,}/g, " ")
    .trim();
}

function sentenceIncludesWord(sentence, word) {
  const cleanWord = String(word || "").trim().toLowerCase();
  if (!sentence || !cleanWord) return false;
  return String(sentence).toLowerCase().includes(cleanWord);
}

function uniqueLines(lines) {
  const seen = new Set();
  return lines
    .map((line) => String(line || "").trim())
    .filter((line) => {
      const key = line.toLowerCase();
      if (!line || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function createDemoVocabBurst(payload = {}) {
  const word = String(payload.word || "").trim();
  const clean = word || "practice";
  const count = payload.count === 10 ? 10 : 5;
  const mode = payload.mode === "story" ? "story" : "sentence";
  const length = payload.length || "normal";
  const vocab = payload.vocab || {};
  const textbookWord = vocab.cItem || vocab.mItem || vocab.wAnswer || clean;
  const textbookSentence = fillBlank(vocab.sentence, textbookWord);
  const speakingLine = sentenceIncludesWord(vocab.speaking, clean) ? vocab.speaking : "";
  const meaningLine = vocab.meaning
    ? `In this unit, "${clean}" connects to this idea: ${vocab.meaning}.`
    : "";
  const sourceLines = uniqueLines([
    textbookSentence && sentenceIncludesWord(textbookSentence, clean) ? textbookSentence : "",
    speakingLine,
    meaningLine
  ]);
  const templates = {
    sentence: {
      short: [
        ...sourceLines,
        `Say "${clean}" clearly.`,
        `Use "${clean}" in one short answer.`,
        `Listen for "${clean}" in the sentence.`
      ],
      normal: [
        ...sourceLines,
        `Try saying the word "${clean}" with clear rhythm and stress.`,
        `Make your own sentence that uses "${clean}" naturally.`,
        `Listen again and notice how "${clean}" works in context.`
      ],
      collocations: [
        `${clean} practice`,
        `${clean} example`,
        `${clean} sentence`,
        `${clean} review`,
        `use "${clean}"`
      ]
    },
    story: [
      ...sourceLines,
      `The student listened to the line and repeated "${clean}" aloud.`,
      `Then the student wrote a new sentence with "${clean}".`,
      `By the end of practice, "${clean}" felt easier to remember.`
    ]
  };
  const source = mode === "story" ? templates.story : templates.sentence[length] || templates.sentence.normal;
  return {
    ok: true,
    isDemo: true,
    sentences: Array.from({ length: count }, (_, index) => ({
      id: `demo-${index}`,
      text: source[index % source.length],
      targetWord: clean,
      hasTargetWord: true
    }))
  };
}

function normalizeSentences(data) {
  const rawLines = Array.isArray(data?.sentences)
    ? data.sentences
    : Array.isArray(data?.lines)
      ? data.lines
      : [];

  return rawLines
    .map((item, index) => {
      if (typeof item === "string") {
        return { id: `line-${index}`, text: item, translation: "" };
      }
      if (!item || typeof item !== "object") return null;
      const text = item.text || item.output || item.sentence || "";
      if (!text) return null;
      return {
        id: item.id || `line-${index}`,
        text,
        translation: item.translation || "",
        hasTargetWord: item.hasTargetWord
      };
    })
    .filter(Boolean);
}

export async function generateVocabBurst(payload) {
  if (!isVocabBurstConfigured()) {
    if (canUseDemoFallback()) return createDemoVocabBurst(payload);
    const error = new Error("Vocab Burst is not connected yet. Textbook practice still works.");
    error.code = "not_configured";
    throw error;
  }

  const clientId = getPremiumTtsClientId();
  let response;
  try {
    response = await fetch(`${apiBaseUrl.replace(/\/$/, "")}/generate-vocab-burst`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${anonKey}`,
        apikey: anonKey,
        "Content-Type": "application/json",
        "x-device-id": clientId
      },
      body: JSON.stringify({
        app: "sielpa",
        clientId,
        outputLanguage: "en",
        level: "B1",
        ...payload
      })
    });
  } catch (fetchError) {
    if (canUseDemoFallback()) return createDemoVocabBurst(payload);
    const error = new Error("Vocab Burst backend is not reachable yet. Textbook practice still works.");
    error.code = "network_failed";
    error.cause = fetchError;
    throw error;
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.error || data.ok === false) {
    if (canUseDemoFallback() && (response.status === 404 || data.code === "NOT_FOUND")) {
      return createDemoVocabBurst(payload);
    }
    const error = new Error(data.message || data.error || "Vocab Burst is unavailable right now. Textbook practice still works.");
    error.code = data.code || data.error || "request_failed";
    error.data = data;
    throw error;
  }

  return {
    ...data,
    sentences: normalizeSentences(data)
  };
}
